import type {
  IAdministrationInfo,
  IMedication,
  IStudent,
} from 'src/interfaces/family'
import dayjs from 'dayjs'
import { cloneDeep, isEqual } from 'lodash-es'
import { toDeepRaw } from 'src/utils/common'
import { medicationTemplate } from 'src/templates/family'
import { DATE_FORMATS } from 'src/config/constants'

export interface IStudentMedicationCacheItem {
  medication: IMedication
  isApproved: boolean
}

type AdministrationInfoInput = Omit<IAdministrationInfo, 'lastCompleted'>

type MedicationInput = Omit<
  IMedication,
  'guardianSignature' | 'submissionDate' | 'isNew' | 'administrationInfo'
> & {
  administrationInfo: AdministrationInfoInput
}

const EMPTY_DATE_SENTINEL = '0001-01-01T00:00:00'

export function findStudentById(
  students: IStudent[],
  studentId: string,
): IStudent | undefined {
  if (!students || !studentId) return undefined
  return students.find((student) => student.id === studentId)
}

export function isSameMedicationValues(
  firstMedication: IMedication,
  secondMedication: IMedication,
): boolean {
  return isEqual(
    toDeepRaw(toMedicationComparableInput(firstMedication)),
    toDeepRaw(toMedicationComparableInput(secondMedication)),
  )
}

export function signMedication(
  medication: IMedication,
  cache: IStudentMedicationCacheItem[],
  guardianSignature: string,
  submissionDate: string,
): void {
  const medCache = getMedicationCacheItem(cache, medication)
  if (medCache) {
    medCache.medication = {
      ...medication,
      guardianSignature,
      submissionDate,
    }
  }
}

export function hasMedicationSignature(medication: IMedication): boolean {
  const guardianSignature = medication.guardianSignature?.trim()
  const submissionDate = medication.submissionDate?.trim()
  return Boolean(
    guardianSignature &&
    submissionDate &&
    !submissionDate.startsWith(EMPTY_DATE_SENTINEL),
  )
}

export function toMedicationComparableInput(
  medication: IMedication,
): MedicationInput {
  const {
    id: _id,
    guardianSignature: _guardianSignature,
    submissionDate: _submissionDate,
    isNew: _isNew,
    administrationInfo,
    ...medicationInput
  } = medication

  const { lastCompleted: _lastCompleted, ...administrationInfoInput } =
    administrationInfo

  return {
    ...medicationInput,
    administrationInfo: administrationInfoInput,
  }
}

export function createMedicationCopy(medication: IMedication): IMedication {
  const medicationInput = toMedicationComparableInput(medication)
  const medicationFromTemplate = cloneDeep(medicationTemplate) as IMedication
  const startDate = dayjs(medication.administrationInfo.endDate)
  const endDate = startDate.add(7, 'day')
  const expiryDate = startDate.add(7, 'days')

  return {
    ...medicationFromTemplate,
    ...medicationInput,
    id: crypto.randomUUID(),
    administrationInfo: {
      ...medicationInput.administrationInfo,
      startDate: startDate.format(DATE_FORMATS.date),
      endDate: endDate.format(DATE_FORMATS.date),
      lastCompleted: undefined,
    },
    expiryDate: expiryDate.format(DATE_FORMATS.date),
  }
}

export function getMedicationCacheItem(
  cache: IStudentMedicationCacheItem[],
  medication: IMedication,
): IStudentMedicationCacheItem | undefined {
  return cache.find((item) => item.medication == medication)
}

export function isMedicationInCache(
  cache: IStudentMedicationCacheItem[],
  medication: IMedication,
): boolean {
  const existingItem = getMedicationCacheItem(cache, medication)
  return existingItem ? true : false
}

export function isMedicationApproved(
  cache: IStudentMedicationCacheItem[],
  medication: IMedication,
): boolean {
  const existingItem = getMedicationCacheItem(cache, medication)
  return existingItem ? existingItem.isApproved : false
}

export function addMedicationToCache(
  cache: IStudentMedicationCacheItem[],
  medication: IMedication,
  isApproved: boolean,
): void {
  if (isMedicationInCache(cache, medication)) return
  const cacheItem = createMedicationCacheItem(medication, isApproved)
  cache.push(cacheItem)
}

export function removeMedicationFromCache(
  cache: IStudentMedicationCacheItem[],
  medication: IMedication,
): void {
  const existingItem = getMedicationCacheItem(cache, medication)
  if (
    existingItem &&
    isMedicationInCache(cache, medication) &&
    !isMedicationApproved(cache, medication)
  ) {
    const index = cache.indexOf(existingItem)
    cache.splice(index, 1)
  }
}

export function createMedicationCacheItem(
  medication: IMedication,
  isApproved: boolean,
): IStudentMedicationCacheItem {
  return {
    medication,
    isApproved,
  }
}

export function createApprovedMedicationCacheItems(
  approvedMedications: IMedication[],
): IStudentMedicationCacheItem[] {
  if (!approvedMedications) return []
  return approvedMedications.map((medication) =>
    createMedicationCacheItem(medication, true),
  )
}

export function isAnyMedicationsCopies(medications: IMedication[]): boolean {
  for (let i = 0; i < medications.length; i++) {
    for (let j = medications.length - 1; j > i; j--) {
      if (isSameMedicationValues(medications[i], medications[j])) return true
    }
  }
  return false
}
